import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAppConfig, useBooking, useConfirmPayment, useStartPayment } from "@/hooks/queries";
import { errorMessage } from "@/lib/api";
import { PAYMENT_LABELS, fmtDateTime, money } from "@/lib/format";

const buildUrl = (method, config, amount) => {
  const value = amount.toFixed(2);
  return method === "paypal"
    ? `https://www.paypal.com/paypalme/${config.paypal_handle}/${value}USD`
    : `https://cash.app/$${config.cashtag}/${value}`;
};

const METHODS = [
  { id: "paypal", mark: "P", sub: (c) => `paypal.me/${c.paypal_handle}` },
  { id: "cashapp", mark: "$", sub: (c) => `$${c.cashtag}` },
];

export default function Pay() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: b, isLoading } = useBooking(id);
  const { data: config } = useAppConfig();
  const start = useStartPayment();
  const confirm = useConfirmPayment();
  const [chosen, setChosen] = useState(null);

  if (isLoading || !config) return <div className="h-96 animate-pulse rounded-3xl bg-surface" data-testid="pay-loading" />;
  if (!b) return null;
  if (b.quote_status === "pending")
    return (
      <div className="lux-card mx-auto max-w-xl p-8 text-center" data-testid="pay-quote-pending">
        <p className="text-ivory">Your rate is still being confirmed by Luxury Elite ATL.</p>
        <Link to={`/trips/${id}`} className="btn-ghost mt-4">Back to trip</Link>
      </div>
    );

  const method = chosen || (b.payment_status === "pending" ? b.payment_method : null);
  const finish = () =>
    confirm.mutate(
      { id, method },
      {
        onSuccess: () => {
          toast.success("Payment recorded — thank you");
          navigate(`/trips/${id}`);
        },
        onError: (e) => toast.error(errorMessage(e)),
      },
    );

  return (
    <div className="mx-auto max-w-xl space-y-6" data-testid="pay-page">
      <Link to={`/trips/${id}`} className="inline-flex items-center gap-2 text-sm text-mist hover:text-gold" data-testid="pay-back-link">
        <ArrowLeft className="h-4 w-4" /> Back to trip
      </Link>
      <div className="rise text-center">
        <p className="eyebrow">Settle your ride</p>
        <p className="gold-text mt-3 font-display text-6xl tracking-tight" data-testid="pay-amount">{money(b.fare)}</p>
        <p className="mt-2 text-sm text-mist">{b.vehicle_name} · {fmtDateTime(b.scheduled_at)}</p>
      </div>

      {b.payment_status === "paid" ? (
        <div className="lux-card p-6 text-center" data-testid="pay-already-paid">
          <CheckCircle2 className="mx-auto h-8 w-8 text-gold" />
          <p className="mt-3 text-ivory">Paid via {PAYMENT_LABELS[b.payment_method]}</p>
        </div>
      ) : b.status === "cancelled" ? (
        <div className="lux-card p-6 text-center text-mist" data-testid="pay-cancelled">This booking was cancelled.</div>
      ) : (
        <>
          {b.payment_verification === "not_received" && (
            <div className="rounded-2xl border border-cancelled/30 bg-cancelled/10 p-4 text-sm text-ivory" data-testid="pay-not-received-notice">
              We couldn't confirm your last payment. Please try again with PayPal or Cash App.
            </div>
          )}
          <div className="rise stagger-1 space-y-3">
            {METHODS.map((m) => (
              <a
                key={m.id}
                href={buildUrl(m.id, config, b.fare)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  setChosen(m.id);
                  start.mutate({ id, method: m.id });
                }}
                data-testid={`pay-method-${m.id}`}
                className={`lux-card group flex items-center gap-4 p-5 transition-[border-color,transform] duration-300 hover:-translate-y-0.5 hover:border-gold/50 ${method === m.id ? "border-gold" : ""}`}
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-full border border-gold/30 bg-gold/10 font-display text-xl text-gold">{m.mark}</span>
                <span className="flex-1">
                  <span className="block font-display text-xl text-ivory">Pay with {PAYMENT_LABELS[m.id]}</span>
                  <span className="block text-xs text-mist">{m.sub(config)}</span>
                </span>
                <ArrowUpRight className="h-5 w-5 text-mist transition-colors group-hover:text-gold" />
              </a>
            ))}
          </div>
          <div className="rise stagger-2 lux-card space-y-4 p-5">
            <p className="flex gap-3 text-sm text-mist">
              <ShieldCheck className="h-5 w-5 shrink-0 text-gold" />
              {method
                ? `Complete the ${PAYMENT_LABELS[method]} payment in the new tab, then return here to confirm.`
                : "Choose a method — a secure payment page opens in a new tab."}
            </p>
            <button type="button" onClick={finish} disabled={!method || confirm.isPending} className="btn-gold w-full" data-testid="confirm-payment-btn">
              {confirm.isPending && <Loader2 className="h-4 w-4 animate-spin" />} I've completed payment
            </button>
          </div>
        </>
      )}
    </div>
  );
}
